import { useState } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import type { Evidence, ProofPassageEntry } from '@/types/api'

export interface HiddenPassageEntry {
  evidence: Evidence
  passage: ProofPassageEntry
}

interface HiddenPassagesSectionProps {
  entries: HiddenPassageEntry[]
  getSourceLabel: (sourceId: string) => string
  onRestore: (sourceId: string, passageId: string) => void
}

const mdComponents: Components = {
  p: ({ children }) => <>{children}</>,
  em: ({ children }) => <i>{children}</i>,
}

export function HiddenPassagesSection({
  entries,
  getSourceLabel,
  onRestore,
}: HiddenPassagesSectionProps) {
  const [isOpen, setIsOpen] = useState(false)

  if (entries.length === 0) return null

  return (
    <div className="mt-0.5">
      <button
        type="button"
        className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider cursor-pointer flex items-center gap-1.5 py-1.5 w-full hover:text-foreground transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`text-[10px] transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}>
          ›
        </span>
        Hidden passages ({entries.length})
      </button>

      {isOpen && (
        <div className="space-y-1.5">
          {entries.map(({ evidence, passage }) => {
            const pages = Array.from(new Set(passage.boxes.map((b) => b.page_number))).sort((a, b) => a - b)
            const pageLabel = pages.length === 0
              ? ''
              : pages.length === 1
                ? `p.${pages[0]}`
                : `pp.${pages.join(', ')}`

            return (
              <div
                key={`${evidence.source_doc}:${passage.passage_id}`}
                className="bg-muted rounded-lg opacity-55 overflow-hidden"
              >
                <div className="flex items-start gap-1.5 px-2.5 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-medium text-foreground leading-tight">
                      <ReactMarkdown components={mdComponents}>
                        {getSourceLabel(evidence.source_doc)}
                      </ReactMarkdown>
                    </div>
                    {passage.passage_text && (
                      <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug line-clamp-3">
                        "{passage.passage_text}"
                      </div>
                    )}
                    {pageLabel && (
                      <div className="text-[9px] text-muted-foreground/70 mt-0.5">{pageLabel}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="text-[10px] px-2 py-0.5 rounded-md text-primary hover:bg-primary/10 shrink-0 transition-colors"
                    onClick={() => onRestore(evidence.source_doc, passage.passage_id)}
                  >
                    Restore
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
